import { Box, Typography, Button, TextField, Grid, Alert } from '@mui/material';
import { formatBRL } from '../../../utils/formatBRL';
import { mergeTheme } from '../types';
import type { ThemeCustomization } from '../types';

export type TicketQuantities = Record<string, number>;

interface TicketOption {
  type: string;
  label: string;
  value: number;
  description?: string;
  available?: number | null;
}

interface TicketSelectorProps {
  tickets: TicketOption[];
  quantities: TicketQuantities;
  onQuantityChange: (type: string, quantity: number) => void;
  eventTitle?: string;
  themeCustomization?: ThemeCustomization;
  totalAvailable?: number | null;
  registrationTitle?: string;
  registrationSubtitle?: string;
  maxTotalQuantity?: number;
}

export function TicketSelector({
  tickets,
  quantities,
  onQuantityChange,
  eventTitle = 'este evento',
  themeCustomization,
  totalAvailable,
  registrationTitle,
  registrationSubtitle,
  maxTotalQuantity = 5,
}: TicketSelectorProps) {
  const mergedTheme = mergeTheme(themeCustomization);
  const primaryColor = mergedTheme.colors?.primaryColor || '#00de76';
  const textColor = mergedTheme.colors?.textColor || '#222222';
  const textSecondaryColor = mergedTheme.colors?.textSecondaryColor || '#424242';

  const isTotalSoldOut = totalAvailable !== null && totalAvailable !== undefined && totalAvailable <= 0;

  const totalSelected = tickets.reduce(
    (sum, ticket) => sum + (quantities[ticket.type] || 0),
    0,
  );

  const getMaxForType = (type: string): number => {
    const ticket = tickets.find((t) => t.type === type);
    const currentQty = quantities[type] || 0;
    const remainingGlobal = maxTotalQuantity - totalSelected + currentQty;
    const typeAvailable = ticket?.available ?? maxTotalQuantity;
    const eventRemaining = totalAvailable ?? maxTotalQuantity;
    return Math.max(0, Math.min(remainingGlobal, typeAvailable, eventRemaining));
  };

  const handleQuantityChange = (type: string, delta: number) => {
    const current = quantities[type] || 0;
    const maxForType = getMaxForType(type);
    const next = Math.max(0, Math.min(maxForType, current + delta));
    if (next !== current) {
      onQuantityChange(type, next);
    }
  };

  const handleInputChange = (type: string, value: string) => {
    const parsed = parseInt(value, 10);
    const next = Number.isNaN(parsed) ? 0 : parsed;
    const maxForType = getMaxForType(type);
    onQuantityChange(type, Math.max(0, Math.min(maxForType, next)));
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="h5"
        fontWeight={700}
        gutterBottom
        sx={{
          color: textColor,
          fontSize: '1.95rem',
          mb: 1,
          fontFamily: mergedTheme.typography?.fontFamily || 'Manrope, sans-serif',
        }}
      >
        {registrationTitle || 'Inscreva-se'}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mb: 2,
          lineHeight: 1.6,
          color: textSecondaryColor,
          fontSize: '0.8125rem',
          fontFamily: mergedTheme.typography?.fontFamily || 'Manrope, sans-serif',
        }}
      >
        {registrationSubtitle ||
          `Selecione os ingressos e preencha os dados para garantir sua vaga em ${eventTitle}.`}
      </Typography>

      {isTotalSoldOut && (
        <Alert severity="error" sx={{ mb: 2 }}>
          As vagas para este evento estão esgotadas. O limite total de participantes foi atingido.
        </Alert>
      )}

      {!isTotalSoldOut &&
        tickets.every(
          (ticket) =>
            ticket.available !== null &&
            ticket.available !== undefined &&
            ticket.available <= 0,
        ) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            As vagas para todos os tipos de ingresso estão esgotadas.
          </Alert>
        )}

      {totalSelected > 0 && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 2,
            fontFamily: 'Manrope, sans-serif',
            color: textSecondaryColor,
          }}
        >
          {totalSelected} ingresso{totalSelected !== 1 ? 's' : ''} selecionado{totalSelected !== 1 ? 's' : ''} (máximo {maxTotalQuantity})
        </Typography>
      )}

      <Grid container spacing={1.5}>
        {tickets.map((ticket) => {
          const quantity = quantities[ticket.type] || 0;
          const isSelected = quantity > 0;
          const hasNoSlots =
            ticket.available !== null && ticket.available !== undefined && ticket.available <= 0;
          const isDisabled = isTotalSoldOut || hasNoSlots;
          const maxForType = getMaxForType(ticket.type);

          return (
            <Grid item xs={12} sm={6} key={ticket.type}>
              <Box
                sx={{
                  p: 1.75,
                  bgcolor: isDisabled ? '#F5F5F5' : '#FFF5F5',
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: isDisabled
                    ? 'rgba(158, 158, 158, 0.5)'
                    : isSelected
                      ? '#FF69B4'
                      : 'rgba(255, 85, 186, 0.5)',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  transform: isSelected && !isDisabled ? 'scale(1.02)' : 'scale(1)',
                  boxShadow: isSelected && !isDisabled ? '0 4px 12px rgba(255, 105, 180, 0.2)' : 'none',
                  opacity: isDisabled ? 0.7 : 1,
                  cursor: isDisabled ? 'not-allowed' : 'default',
                  '&:hover': {
                    transform: isDisabled ? 'scale(1)' : 'scale(1.01)',
                    boxShadow: isDisabled ? 'none' : '0 2px 8px rgba(255, 105, 180, 0.15)',
                  },
                }}
              >
                {hasNoSlots && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      pointerEvents: 'none',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        textAlign: 'center',
                        px: 2,
                        fontSize: '1.25rem',
                        fontFamily: mergedTheme.typography?.fontFamily || 'Manrope, sans-serif',
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                      }}
                    >
                      Esgotado
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    position: 'absolute',
                    right: 82,
                    top: 0,
                    bottom: 0,
                    width: 0,
                    borderLeft: '2px dashed #FFB3BA',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      color: textColor,
                      fontSize: '1rem',
                      fontFamily: mergedTheme.typography?.fontFamily || 'Manrope, sans-serif',
                    }}
                  >
                    {ticket.label}
                  </Typography>
                </Box>

                {ticket.description && (
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.5,
                      color: textSecondaryColor,
                      fontSize: '0.8125rem',
                      fontFamily: mergedTheme.typography?.fontFamily || 'Manrope, sans-serif',
                      maxWidth: 'calc(100% - 70px)',
                      wordBreak: 'break-word',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {ticket.description.length > 70
                      ? ticket.description.slice(0, 70) + '...'
                      : ticket.description}
                  </Typography>
                )}

                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{
                    mb: 1.75,
                    color: textColor,
                    fontSize: '1rem',
                    fontFamily: mergedTheme.typography?.fontFamily || 'Manrope, sans-serif',
                  }}
                >
                  {formatBRL(ticket.value)}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.75 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleQuantityChange(ticket.type, -1)}
                    disabled={quantity === 0 || isDisabled}
                    sx={{
                      minWidth: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: primaryColor,
                      color: 'white',
                      '&:hover': {
                        bgcolor: mergedTheme.colors?.primaryColorHover || primaryColor,
                        opacity: 0.9,
                      },
                      '&:disabled': {
                        bgcolor: '#E0E0E0',
                        color: '#9E9E9E',
                      },
                      fontSize: '1.25rem',
                      fontWeight: 400,
                      boxShadow: 'none',
                    }}
                  >
                    −
                  </Button>
                  <TextField
                    type="number"
                    value={quantity}
                    disabled={isDisabled}
                    onChange={(e) => handleInputChange(ticket.type, e.target.value)}
                    inputProps={{
                      min: 0,
                      max: maxForType,
                      style: {
                        textAlign: 'center',
                        padding: '6px',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      },
                    }}
                    sx={{
                      width: 50,
                      '& .MuiOutlinedInput-root': {
                        height: 32,
                        bgcolor: 'white',
                        fontFamily: 'Manrope, sans-serif',
                        '& fieldset': { borderColor: '#E0E0E0' },
                        '& input': { fontFamily: 'Manrope, sans-serif' },
                      },
                    }}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleQuantityChange(ticket.type, 1)}
                    disabled={quantity >= maxForType || isDisabled}
                    sx={{
                      minWidth: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: primaryColor,
                      color: 'white',
                      '&:hover': {
                        bgcolor: mergedTheme.colors?.primaryColorHover || primaryColor,
                        opacity: 0.9,
                      },
                      '&:disabled': {
                        bgcolor: '#E0E0E0',
                        color: '#9E9E9E',
                      },
                      fontSize: '1.25rem',
                      fontWeight: 400,
                      boxShadow: 'none',
                    }}
                  >
                    +
                  </Button>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
